import { Box, IconButton, Stack } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import React, { Fragment } from "react";
import { useTranslation } from "react-i18next";
import { BiPencil } from "react-icons/bi";
import DetailCard from "../../../../components/display/DetailCard";
import SimpleAlert from "../../../../components/display/SimpleAlert";
import UserHint from "../../../../components/display/UserHint";
import ErrorFullScreenMessage from "../../../../components/fullScreenMessage/ErrorFullScreenMessage";
import Skeletons from "../../../../components/loader/Skeletons";
import { LOGGED_IN_USER } from "../../../../enums/QueryKeys";
import useConfirm from "../../../../hooks/useConfirm";
import useUser from "../../../../hooks/useUser";
import { isNullOrEmpty } from "../../../../utils/Functions";
import { EditingFormUser } from "../../form/EditingInformationForm";
import UserInformationView from "../UserInformationView";
import { useProfileSettingsState } from "../../../../hooks/useProfileSettingsState";

export default function Profile() {
  const queryClient = useQueryClient();
  const { data, isLoading, error, profile, roles } = useUser();
  const [viewEditForm, setViewEditForm] = React.useState(false);

  const { t } = useTranslation();
  const userInformation = UserInformationView(data);

  const { ConfirmationDialog } = useConfirm(
    t("Attention"),
    t("Accept"),
    t("Cancel"),
  );
  const { isForcedEdit } = useProfileSettingsState();

  const handleViewEditForm = () => {
    setViewEditForm((prev) => !prev);
  };

  React.useEffect(() => {
    if (isForcedEdit) {
      setViewEditForm(true);
    }
  }, [isForcedEdit]);

  if (isLoading) {
    return <Skeletons numOfSkeletons={5} grid={false} height={50} />;
  } else if (error) {
    return <ErrorFullScreenMessage />;
  }

  const alertCondition = roles.employee && isNullOrEmpty(profile.email);
  return (
    <Fragment>
      <Stack
        direction={"row"}
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
        mb={2}
      >
        <GoogleAcc googleid={profile.googleid} />
        <IconButton
          onClick={handleViewEditForm}
          title={t("Edit")}
          data-testid="profile.details.edit-button"
        >
          <BiPencil />
        </IconButton>
      </Stack>
      <SimpleAlert
        message={"ClinicSharesEmailUserMessage"}
        severity={"info"}
        show={alertCondition}
        mb={1}
      />

      {viewEditForm ? (
        <EditingFormUser
          setViewEditForm={setViewEditForm}
          profileProp={data}
        />
      ) : (
        <DetailCard valueArray={userInformation} />
      )}

      <CreatedAt createdAt={profile.createdAT} />
      <LastLogin lastLogin={profile.lastLogin} />
      <ConfirmationDialog />

      <br />
    </Fragment>
  );
}

function CreatedAt({ createdAt = null }) {
  return <Hint message={"AccountCreatedAt"} value={createdAt} />;
}

function LastLogin({ lastLogin = null }) {
  return <Hint message={"LastLogin"} value={lastLogin} />;
}

function GoogleAcc({ googleid = null }) {
  if (googleid) {
    return (
      <Box sx={{ mb: 2 }}>
        <UserHint hint={"AccountCreatedWithGoogle"} />
      </Box>
    );
  }
  return <Box sx={{ mb: 2 }} />;
}

function Hint({ value = null, message = null }) {
  const { t } = useTranslation();
  if (value) {
    return <UserHint hint={`${t(message)} ${value}`} />;
  }
}
